import React, { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, ArrowRightIcon, StarIcon, MessageCircleIcon, LinkIcon, RefreshCwIcon, ShieldCheckIcon, ThumbsUpIcon, ChevronDownIcon, BuildingIcon, ZapIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
export function ReputationManagementPage() {
  const faqs = [{
    question: 'How does automated review requesting work?',
    answer: 'When a job is marked as complete in your CRM or invoicing software, our system automatically sends a polite, customized text message and email to the customer thanking them for their business and asking for a review with a direct link.'
  }, {
    question: 'Can I respond to reviews from one dashboard?',
    answer: 'Yes. You can view and reply to reviews from Google, Facebook, and major review platforms all from a single unified dashboard or directly from our mobile app. No more logging into multiple accounts.'
  }, {
    question: 'What platforms do you manage reviews on?',
    answer: 'We focus on the platforms that matter most for local service businesses: Google Business Profile, Facebook, and the major review sites for your industry. We can also integrate with industry-specific platforms depending on your needs.'
  }, {
    question: 'Do I need to be on every review platform?',
    answer: 'No. We analyze your specific market and industry to determine where your customers are actually looking. Usually, dominating Google plus one or two key review platforms is all a local service business needs to win their market.'
  }];
  const FAQItem = ({
    question,
    answer
  }: {
    question: string;
    answer: string;
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    return <div className="border-b border-gray-800 last:border-0" data-id="element-2635" data-mp="212imt43xw0l8">
        <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-2636" data-mp="3vdiebipouoe1">
          <span className="text-lg font-bold text-white group-hover:text-yellow-500 transition-colors" data-id="element-2637" data-mp="3ln5y22owphdp">
            {question}
          </span>
          <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-yellow-500' : ''}`} data-id="element-2638" data-mp="3qjo18y4l97nx" />
        </button>
        <AnimatePresence data-id="element-2639" data-mp="3hips61fam9yh">
          {isOpen && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-2640" data-mp="32obi4qdpiavy">
              <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2641" data-mp="2leh8z6dsj1me">{answer}</p>
            </motion.div>}
        </AnimatePresence>
      </div>;
  };
  return <div className="bg-viridia-black min-h-screen" data-id="element-2642" data-mp="3056r6spo5gzr">
      <SEOHead title="Reputation Management | Automate Reviews | Viridia Analytics" description="Turn happy customers into public proof. Manage reviews across platforms, automate review requests, and build trust for your local service business." canonical="/reputation-management" keywords="reputation management Phoenix, marketing agency phoenix, HVAC, painters, landscapers, contractors, window installers, automated review requests, get more Google reviews, local business reputation, review management software" faq={faqs} data-id="element-2643" data-mp="2vbc9tvxjde3u" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden lg:min-h-[90vh] flex items-center" data-id="element-2644" data-mp="1jpvo9k57cnmg">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2645" data-mp="1l5hlv57ke31r">
          <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-yellow-500/10 blur-[120px] animate-pulse-slow" data-id="element-2646" data-mp="1q3j4xq4zq2d8"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2647" data-mp="203thd8j7b5rc">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2648" data-mp="12frrukbmllaw">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2649" data-mp="17dte1e4cm0ql">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-2650" data-mp="17ew4jkxre15z">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-sm font-semibold tracking-wide mb-6" data-id="element-2651" data-mp="oo4v6jdruarl">
                  REPUTATION MANAGEMENT
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-2652" data-mp="2xy4a2jh02bfe">
                  Turn Happy Customers Into{' '}
                  <span className="text-yellow-500" data-id="element-2653" data-mp="2nzepuk1msevz">Public Proof</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2654" data-mp="2j024y4ublhw1">
                  Reviews are not just vanity metrics; they are conversion
                  leverage. We automate review requests after completed jobs to
                  build trust and help you win more local searches.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2655" data-mp="2596rsgcsgemn">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2656" data-mp="209141b9n3c9u">
                    Book a Strategy Call
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2657" data-mp="1qmq9gilozsx9">
                    View Packages
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Visual - Reputation Control Center */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block h-[500px]" data-id="element-2658" data-mp="1vjgn6za0ycfi">
              <div className="absolute inset-0 bg-gradient-to-br from-gray-900 to-viridia-surface rounded-2xl border border-gray-800 shadow-2xl overflow-hidden p-6 flex flex-col" data-id="element-2659" data-mp="gmdptciq3g09">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-800" data-id="element-2660" data-mp="qdrtlwhpj1z7">
                  <div className="text-sm font-bold text-white flex items-center" data-id="element-2661" data-mp="ldm2chfcnhhn">
                    <StarIcon className="w-4 h-4 text-yellow-500 mr-2 fill-current" data-id="element-2662" data-mp="3ttp5qazfik06" />
                    Reputation Control Center
                  </div>
                  <div className="bg-green-500/10 text-green-400 text-xs px-2 py-1 rounded font-medium" data-id="element-2663" data-mp="3owp0u3ebfnan">
                    System Active
                  </div>
                </div>

                <div className="flex-grow relative" data-id="element-2664" data-mp="2muw3oqns77h">
                  {/* Floating Review Cards */}
                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.5
                }} className="absolute top-4 left-4 right-12 bg-white rounded-lg p-4 shadow-lg z-20" data-id="element-2665" data-mp="1j0l2x395x9kt">
                    <div className="flex justify-between items-start mb-2" data-id="element-2666" data-mp="3mapn47jskwhv">
                      <div className="flex text-yellow-400" data-id="element-2667" data-mp="3r9stpwff7ary">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2668" data-mp="3vacquzzzuee3" data-mpi={i} />)}
                      </div>
                      <span className="text-xs text-gray-400 font-medium" data-id="element-2669" data-mp="45crmy4vcbb8">
                        Google • 2h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-800 font-medium mb-1" data-id="element-2670" data-mp="25wzgat1afu4j">
                      "They responded instantly and fixed the issue the same
                      day. Highly recommend!"
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2671" data-mp="23db8lhzc6hvw">- Sarah Jenkins</p>
                  </motion.div>

                  <motion.div initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.7
                }} className="absolute top-32 left-12 right-4 bg-viridia-black border border-gray-700 rounded-lg p-4 shadow-xl z-10" data-id="element-2672" data-mp="1yjgpogrk26xv">
                    <div className="flex justify-between items-start mb-2" data-id="element-2673" data-mp="2calnref9yr63">
                      <div className="flex text-yellow-500" data-id="element-2674" data-mp="27ck2cmudmfrg">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" data-id="element-2675" data-mp="10ykt5ivqc8if" data-mpi={i} />)}
                      </div>
                      <span className="text-xs text-gray-500 font-medium" data-id="element-2676" data-mp="2nrvh57u8gl8a">
                        Review Platform • 1h ago
                      </span>
                    </div>
                    <p className="text-sm text-gray-300 font-medium mb-1" data-id="element-2677" data-mp="2iqz5g83n3usb">
                      "Very professional crew. The estimate was accurate and the
                      work was top notch."
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2678" data-mp="36icua42l188j">- Michael T.</p>
                  </motion.div>

                  {/* Automation Flow Indicator */}
                  <div className="absolute bottom-4 left-0 right-0 flex justify-center" data-id="element-2679" data-mp="32grmgx4wnk57">
                    <div className="bg-viridia-surface border border-gray-800 rounded-full px-4 py-2 flex items-center gap-3 shadow-lg" data-id="element-2680" data-mp="3ggahco8pgyzk">
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2681" data-mp="1m2rp9txg28ln">
                        <CheckCircleIcon className="w-3 h-3 text-viridia" data-id="element-2682" data-mp="1m4vtye363bmz" /> Job
                        Complete
                      </div>
                      <ArrowRightIcon className="w-3 h-3 text-gray-600" data-id="element-2683" data-mp="3sqid9yssms0b" />
                      <div className="flex items-center gap-1 text-xs text-gray-400" data-id="element-2684" data-mp="1kqwvr4titqp">
                        <MessageCircleIcon className="w-3 h-3 text-blue-400" data-id="element-2685" data-mp="1ml98h20xdj6" />{' '}
                        SMS Sent
                      </div>
                      <ArrowRightIcon className="w-3 h-3 text-gray-600" data-id="element-2686" data-mp="3nzjr4il4izp5" />
                      <div className="flex items-center gap-1 text-xs text-yellow-500 font-bold" data-id="element-2687" data-mp="3a0tv76a1j62d">
                        <StarIcon className="w-3 h-3 fill-current" data-id="element-2688" data-mp="3ezdipb4a8pwu" /> 5-Star
                        Review
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2689" data-mp="yark3lpjh25w">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2690" data-mp="139udg8w5pjy1">
          <ScrollReveal data-id="element-2691" data-mp="138029maot675">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2692" data-mp="11snmv2n4otmz">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2693" data-mp="11u8eoki25yh5">
                Why Reviews Matter Financially
              </h2>
              <p className="text-xl text-gray-400" data-id="element-2694" data-mp="ru7vqzo3pkdh">
                In local services, trust is everything. If your competitor has
                100 more reviews than you, they are getting the call.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2695" data-mp="1klj38v0b9z5v">
            {[{
            icon: <StarIcon className="w-6 h-6 text-white" data-id="element-2696" data-mp="1fmodta7u9ccs" />,
            title: 'Conversion Leverage',
            desc: 'A strong review profile increases the conversion rate of every ad dollar you spend.',
            color: 'bg-yellow-500'
          }, {
            icon: <ShieldCheckIcon className="w-6 h-6 text-white" data-id="element-2697" data-mp="23br416gjgdiw" />,
            title: 'Local Trust',
            desc: 'Customers hire the business that looks the most reliable. Reviews are public proof of your reliability.',
            color: 'bg-blue-500'
          }, {
            icon: <RefreshCwIcon className="w-6 h-6 text-white" data-id="element-2698" data-mp="3q6m7gplyy18g" />,
            title: 'Automated Workflows',
            desc: 'We tie review requests directly to your CRM. When a job is done, the request goes out automatically.',
            color: 'bg-viridia'
          }].map((feature, i) => <ScrollReveal key={i} delay={i * 0.1} data-id="element-2699" data-mp="8xzcgfk0luel" data-mpi={i}>
                <div className="bg-viridia-black rounded-2xl p-8 border border-gray-800 h-full hover:border-gray-700 transition-colors" data-id="element-2700" data-mp="8weesh2elvci">
                  <div className={`w-12 h-12 rounded-xl ${feature.color}/20 flex items-center justify-center mb-6`} data-id="element-2701" data-mp="hrcb7y4rfq8l">
                    <div className={`w-8 h-8 rounded-full ${feature.color} flex items-center justify-center`} data-id="element-2702" data-mp="mnsxsnwb9wjk">
                      {feature.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-2703" data-mp="93q7dzdl7url">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-2704" data-mp="nqrpu4tfgrhs">
                    {feature.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section className="py-24 bg-viridia-black" data-id="element-2705" data-mp="3m9yom3qqbs11">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2706" data-mp="3mb9rm1ccovr3">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2707" data-mp="51v5otrqvfda">
            <ScrollReveal data-id="element-2708" data-mp="2rzv73xx549">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2709" data-mp="1gw37ahvxd5">
                The Automated Review Loop
              </h2>
              <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2710" data-mp="1giuw1juj7k4o">
                Asking for reviews manually is awkward and inconsistent. We
                build a system that asks for you, exactly when the customer is
                happiest.
              </p>

              <div className="space-y-8" data-id="element-2711" data-mp="1o12mynjozu1z">
                <div className="flex items-start" data-id="element-2712" data-mp="1a0i11iihdi49">
                  <div className="flex-shrink-0 mr-4" data-id="element-2713" data-mp="1a1tde6pzry6z">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2714" data-mp="1e1tsltkx0df3">
                      1
                    </div>
                  </div>
                  <div data-id="element-2715" data-mp="26pplsob9zn74">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2716" data-mp="26q92k1p6w5jk">
                      Trigger on Completion
                    </h3>
                    <p className="text-gray-400" data-id="element-2717" data-mp="1x0pmf3sv2pse">
                      When your team marks a job as complete or paid in your
                      CRM, the automation begins.
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-2718" data-mp="2pi1z263gw8w1">
                  <div className="flex-shrink-0 mr-4" data-id="element-2719" data-mp="2zcz43mbfzw70">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2720" data-mp="2zcpehzub7r1u">
                      2
                    </div>
                  </div>
                  <div data-id="element-2721" data-mp="2qedqm5ktv7d3">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2722" data-mp="2ldzf2dk0pfab">
                      Smart Request Sent
                    </h3>
                    <p className="text-gray-400" data-id="element-2723" data-mp="2gfxwruuf1jzw">
                      A personalized text and email are sent thanking them for
                      their business and providing a direct link to review.
                    </p>
                  </div>
                </div>

                <div className="flex items-start" data-id="element-2724" data-mp="22nrjcgindr45">
                  <div className="flex-shrink-0 mr-4" data-id="element-2725" data-mp="22kvm6uemj6gr">
                    <div className="w-10 h-10 rounded-full bg-viridia/10 border border-viridia/30 flex items-center justify-center text-viridia font-bold" data-id="element-2726" data-mp="1xmu163xn0x2n">
                      3
                    </div>
                  </div>
                  <div data-id="element-2727" data-mp="1iklxarmmwdzx">
                    <h3 className="text-xl font-bold text-white mb-2" data-id="element-2728" data-mp="1njyhf4m4f6yt">
                      Monitor & Respond
                    </h3>
                    <p className="text-gray-400" data-id="element-2729" data-mp="1sirs6pa0wjva">
                      New reviews appear in your unified dashboard, allowing you
                      to respond quickly and professionally.
                    </p>
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="left" data-id="element-2730" data-mp="14uie69j1x0ep">
              <div className="bg-viridia-surface rounded-2xl p-8 border border-gray-800 shadow-2xl" data-id="element-2731" data-mp="14r4amxynil5o">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800" data-id="element-2732" data-mp="19qp8t65zyphc">
                  <h3 className="text-white font-bold" data-id="element-2733" data-mp="h3w6ijcyli58">Automated SMS</h3>
                  <span className="text-xs text-gray-500" data-id="element-2734" data-mp="h3mgivrwzazj">Sent at 4:30 PM</span>
                </div>
                <div className="space-y-4" data-id="element-2735" data-mp="m14syw5548ca">
                  <div className="flex justify-end" data-id="element-2736" data-mp="208w7x7vzqk37">
                    <div className="bg-blue-600 text-white p-3 rounded-2xl rounded-tr-sm max-w-[80%] text-sm" data-id="element-2737" data-mp="209piyl1zfvr5">
                      Hi John, thanks for choosing Viridia HVAC! We hope you're
                      happy with your new AC unit. If you have a minute, we'd
                      love your feedback: https://g.page/r/review
                    </div>
                  </div>
                  <div className="flex justify-start" data-id="element-2738" data-mp="2j6qpiab6q3x7">
                    <div className="bg-gray-800 text-white p-3 rounded-2xl rounded-tl-sm max-w-[80%] text-sm" data-id="element-2739" data-mp="2f6ygxxlsjxru">
                      You guys were great. Just left a 5-star review!
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Best Fit Section */}
      <section className="py-20 bg-viridia-surface border-t border-gray-800" data-id="element-2740" data-mp="32twryfr56did">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2741" data-mp="2xz0yrca65k3o">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-2742" data-mp="3hiq01cu8mdvd">
            <ScrollReveal data-id="element-2743" data-mp="3lkl60un45ffh">
              <h2 className="text-3xl font-bold text-white mb-6" data-id="element-2744" data-mp="3lkuvmh48xkkn">
                Who is this for?
              </h2>
              <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-2745" data-mp="37k0k3plwj3hr">
                Our reputation management system is designed for local service
                businesses that want to turn their happy customers into a
                marketing asset.
              </p>
              <ul className="space-y-4" data-id="element-2746" data-mp="32m8ux3cn5vh9">
                <li className="flex items-start gap-3" data-id="element-2747" data-mp="32na75b5e8o0k">
                  <BuildingIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-2748" data-mp="2ouu78j7sh63v" />
                  <div data-id="element-2749" data-mp="2tt5garq9fcl1">
                    <h4 className="font-bold text-white" data-id="element-2750" data-mp="2tugkgsnmamco">Local Contractors</h4>
                    <p className="text-sm text-gray-400" data-id="element-2751" data-mp="2evmm39utc767">
                      Businesses that rely on local trust to win jobs.
                    </p>
                  </div>
                </li>
                <li className="flex items-start gap-3" data-id="element-2752" data-mp="2izas3fjpf856">
                  <ZapIcon className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-2753" data-mp="2j1wy3aqy5fla" />
                  <div data-id="element-2754" data-mp="2o0hyba677z9q">
                    <h4 className="font-bold text-white" data-id="element-2755" data-mp="2nwke0l7vx1o9">
                      Businesses Using Our CRM
                    </h4>
                    <p className="text-sm text-gray-400" data-id="element-2756" data-mp="2e07m9svwgxw5">
                      The review automation ties perfectly into our CRM,
                      triggering requests automatically when jobs are completed.
                    </p>
                  </div>
                </li>
              </ul>
            </ScrollReveal>
            <ScrollReveal delay={0.2} data-id="element-2757" data-mp="2rzgxsf95v3ts">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8" data-id="element-2758" data-mp="2ryfmc9o8gfbj">
                <h3 className="text-xl font-bold text-white mb-4" data-id="element-2759" data-mp="2wxs80r3dbgcj">
                  Related Services
                </h3>
                <div className="space-y-4" data-id="element-2760" data-mp="3pfw6wdlolkts">
                  <Link to="/crm-business-tools" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2761" data-mp="3pgxsvd48jrqa">
                    <div className="font-bold text-white mb-1" data-id="element-2762" data-mp="191khkdj9xisi">
                      CRM & Business Tools
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2763" data-mp="1irvvmswo98d4">
                      Trigger review requests automatically from your pipeline.
                    </div>
                  </Link>
                  <Link to="/web-design-seo" className="block p-4 bg-viridia-surface rounded-xl border border-gray-800 hover:border-viridia/50 transition-colors" data-id="element-2764" data-mp="q2yeit475hly">
                    <div className="font-bold text-white mb-1" data-id="element-2765" data-mp="100mkygb60djy">
                      Conversion Websites
                    </div>
                    <div className="text-sm text-gray-400" data-id="element-2766" data-mp="2fglheubf6l4d">
                      Showcase your 5-star reviews on a site built to convert.
                    </div>
                  </Link>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2767" data-mp="1d6l2v353o1tk">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-2768" data-mp="1i4356aom3isl">
          <ScrollReveal data-id="element-2769" data-mp="1m454id6vk04p">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2770" data-mp="1m4dk4cudtjjl">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2771" data-mp="uf21t1umyxu6">
          <ScrollReveal delay={0.2} data-id="element-2772" data-mp="yfubfw7f74qz">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-2773" data-mp="yesp2vkyevty">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2774" data-mp="1385e36tjp4n8" data-mpi={index} />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-2775" data-mp="35yusdwqhpsz">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2776" data-mp="3uduwmhenj5qm">
          <ScrollReveal data-id="element-2777" data-mp="3qayc2l5jwzk1">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2778" data-mp="3qa5cq58x9cbx">
              Start building local trust
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-2779" data-mp="3le7u1sa6rtvz">
              Book a strategy call to see how we can automate your review
              requests and help you win more local searches.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-lg bg-viridia text-white hover:bg-viridia-dark transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.2)]" data-id="element-2780" data-mp="36n7c8wnjunn6">
              Book a Strategy Call <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2781" data-mp="2snfz0y4uxba8" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}